import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { StudioConfigService } from './studio-config.service';
import { LocalizationService } from './localization.service';

/**
 * Resolves the studio's currency from the loaded studio configuration and
 * exposes it both reactively (observables) and synchronously (for pipes/templates
 * that need a value immediately).
 *
 * Source of truth, in order of preference:
 *   1. The explicit Currency dropdown on the studio info form (`field_currency_code`).
 *   2. The currency implied by the studio's country (e.g. an Italian studio → EUR).
 *   3. USD, as a final default.
 *
 * This is the single source of truth for "what currency symbol should money be
 * shown in" across the app, replacing hardcoded `$`.
 */
@Injectable({
  providedIn: 'root'
})
export class CurrencyService {
  /** Fallback used before config loads, or when the studio's country is unknown. */
  private static readonly DEFAULT_CURRENCY = 'USD';

  /**
   * Symbols for the currencies AMI studios actually use. Intl can derive these,
   * but a small explicit map keeps the bare symbol stable across locales
   * (Intl renders e.g. 'US$' or 'CA$' in some locales, which we don't want).
   */
  private static readonly CURRENCY_SYMBOLS: { [code: string]: string } = {
    USD: '$', CAD: '$', AUD: '$', NZD: '$', HKD: '$', SGD: '$', MXN: '$',
    EUR: '€', GBP: '£', JPY: '¥', CNY: '¥',
    CHF: 'CHF', SEK: 'kr', NOK: 'kr', DKK: 'kr',
    BRL: 'R$', ZAR: 'R', INR: '₹',
  };

  /**
   * Map the app's UI language codes (the footer language selector) to BCP-47
   * locales so Intl formats money the way that locale expects: German
   * '3.300,00 €', French '3 300,00 €', English '€3,300.00'. Anything not listed
   * falls back to the code itself (e.g. 'fr' → 'fr'), which Intl handles.
   */
  private static readonly UI_LANG_TO_LOCALE: { [lang: string]: string } = {
    en: 'en-US', fr: 'fr-FR', es: 'es-ES', it: 'it-IT', de: 'de-DE',
    zh: 'zh-Hant', ar: 'ar',
  };

  private currencyCode$ = new BehaviorSubject<string>(CurrencyService.DEFAULT_CURRENCY);

  /** Synchronous cache so pipes/templates can read the symbol without subscribing. */
  private _currentCode: string = CurrencyService.DEFAULT_CURRENCY;
  private _currentSymbol: string = '$';

  constructor(
    private studioConfigService: StudioConfigService,
    private localizationService: LocalizationService,
    private translate: TranslateService
  ) {
    // Resolve currency whenever the studio config changes. An explicit currency
    // code (the studio's Currency dropdown) wins; otherwise fall back to the
    // currency implied by the studio's country, then to USD.
    combineLatest([
      this.studioConfigService.getCurrencyCode(),
      this.studioConfigService.getCountryCode(),
    ])
      .pipe(
        map(([explicitCode, countryCode]) =>
          explicitCode
          || this.localizationService.getCurrencyForCountry(countryCode)
          || CurrencyService.DEFAULT_CURRENCY
        ),
        distinctUntilChanged()
      )
      .subscribe(code => {
        this._currentCode = code;
        this._currentSymbol = CurrencyService.symbolForCode(code);
        this.currencyCode$.next(code);
      });
  }

  /**
   * The studio's currency symbol, synchronously (e.g. '€', '$').
   * Safe to call from an impure pipe. Returns the default until config loads,
   * then updates on the next change-detection pass.
   */
  getSymbol(): string {
    return this._currentSymbol;
  }

  /** The studio's ISO currency code, synchronously (e.g. 'EUR', 'USD'). */
  getCurrencyCode(): string {
    return this._currentCode;
  }

  /** Reactive currency code, for components that prefer subscribing. */
  currencyCode(): Observable<string> {
    return this.currencyCode$.asObservable().pipe(distinctUntilChanged());
  }

  /** Reactive currency symbol, for components that prefer subscribing. */
  symbol(): Observable<string> {
    return this.currencyCode().pipe(map(code => CurrencyService.symbolForCode(code)));
  }

  /**
   * Format an amount as money in the studio's currency, laid out per the UI
   * locale (the footer language selector). The locale controls grouping,
   * decimal separator, and symbol placement, so the same amount renders as:
   *   English (en): '€3,300.00'      German (de): '3.300,00 €'
   *   French  (fr): '3 300,00 €'     Italian (it): '3.300,00 €'
   *
   * Accepts already-formatted strings too: some backend (Drupal Views) fields
   * arrive pre-formatted like '$3,300.00'. Any currency symbol and US-style
   * grouping commas are stripped before parsing, then the amount is re-formatted
   * in the studio's currency and UI locale, so '$3,300.00' for a German UI on a
   * UK studio becomes '3.300,00 £'.
   *
   * @param value Amount to format
   * @param decimals Fraction digits (default 2)
   */
  format(value: number | string | null | undefined, decimals: number = 2): string {
    let numericValue: number | null | undefined;
    if (typeof value === 'string') {
      // Backend strings are always US-formatted ('$3,300.00'): strip the symbol
      // and grouping commas, keep digits / decimal point / sign.
      numericValue = parseFloat(value.replace(/[^0-9.-]/g, ''));
    } else {
      numericValue = value;
    }
    if (numericValue === null || numericValue === undefined || isNaN(numericValue as number)) {
      numericValue = 0;
    }

    const locale = this.currentLocale();
    try {
      return this.currencyFormatter(locale, this._currentCode, decimals).format(numericValue as number);
    } catch {
      // Defensive: an unknown currency code would make Intl throw. Fall back to
      // the bare symbol + locale-grouped number rather than crash a template.
      const formatted = (numericValue as number).toLocaleString(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
      return `${this._currentSymbol}${formatted}`;
    }
  }

  // Building an Intl.NumberFormat is far costlier than using one, and the
  // studioCurrency pipe is impure (runs on every change detection pass), so
  // reuse one formatter per locale/currency/precision.
  private formatters = new Map<string, Intl.NumberFormat>();

  private currencyFormatter(locale: string, code: string, decimals: number): Intl.NumberFormat {
    const key = `${locale}|${code}|${decimals}`;
    let formatter = this.formatters.get(key);
    if (!formatter) {
      formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: code,
        // 'narrowSymbol' gives the plain '£'/'$'/'€'. Plain 'symbol' would
        // disambiguate foreign currencies in some locales (French renders GBP as
        // '£GB', USD as '$US'), not what we want for a single-currency studio.
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
      this.formatters.set(key, formatter);
    }
    return formatter;
  }

  /** The BCP-47 locale for the current UI language (footer selector). */
  private currentLocale(): string {
    const lang = (this.translate.currentLang || this.translate.defaultLang || 'en').toLowerCase();
    return CurrencyService.UI_LANG_TO_LOCALE[lang] || lang;
  }

  /** Look up the symbol for an ISO currency code, falling back to the code itself. */
  private static symbolForCode(code: string): string {
    return CurrencyService.CURRENCY_SYMBOLS[code] || code;
  }
}
